/**
 * The plan catalog (ADR 0009): what is sold, for how much, and how many credits it gives. It is
 * code, and `pnpm billing:sync` makes Stripe match it. Prices are DKK excluding VAT.
 */

/**
 * `beta` is never sold: a platform admin gives it to an organisation for the beta (a manual plan
 * with an end date). Grading under it spends no credits; the AI budgets still apply.
 */
export const PLAN_KEYS = ['trial', 'teacher', 'school', 'beta'] as const
export type PlanKey = (typeof PLAN_KEYS)[number]

export const PLAN_INTERVALS = ['month', 'year'] as const
export type PlanInterval = (typeof PLAN_INTERVALS)[number]

export const CURRENCY = 'dkk'

/** Credits every new organisation starts with (the Prøv plan). */
export const TRIAL_CREDITS = 20

/** A submission over this many pages costs two credits (`creditsForSubmission` in grading-core). */
export const LONG_SUBMISSION_PAGES = 12

export interface Entitlements {
  /** Credits one grant gives. For Skole this is per student. */
  submissionsPerPeriod: number
  topupsAllowed: boolean
  /** `null` is no limit. */
  maxMembers: number | null
  doubleGradingAllowed: boolean
  /** Grading spends no credits (the beta plan). */
  unlimitedCredits: boolean
}

export interface PlanPrice {
  /** The Stripe price's lookup key: how the app and `billing:sync` find it. */
  lookupKey: string
  /** In øre (DKK minor units), excluding VAT. Per student for Skole. */
  unitAmount: number
  interval: PlanInterval
  /** Credits one unit gives for one period. */
  credits: number
}

export interface Plan {
  key: PlanKey
  /** Danish name shown to people. */
  name: string
  entitlements: (interval: PlanInterval | null) => Entitlements
  prices: readonly PlanPrice[]
  /** Skole is billed per student with a minimum. */
  perStudent?: { min: number; max: number }
}

const kr = (amount: number) => amount * 100

const TEACHER_ENTITLEMENTS = {
  topupsAllowed: true,
  maxMembers: 1,
  doubleGradingAllowed: true,
  unlimitedCredits: false,
} as const

export const PLANS: Readonly<Record<PlanKey, Plan>> = {
  trial: {
    key: 'trial',
    name: 'Prøv',
    prices: [],
    entitlements: () => ({
      submissionsPerPeriod: TRIAL_CREDITS,
      topupsAllowed: false,
      maxMembers: 5,
      doubleGradingAllowed: true,
      unlimitedCredits: false,
    }),
  },
  teacher: {
    key: 'teacher',
    name: 'Lærer',
    prices: [
      { lookupKey: 'aimfora_teacher_month', unitAmount: kr(129), interval: 'month', credits: 15 },
      { lookupKey: 'aimfora_teacher_year', unitAmount: kr(1290), interval: 'year', credits: 150 },
    ],
    entitlements: (interval) => ({
      submissionsPerPeriod: interval === 'month' ? 15 : 150,
      ...TEACHER_ENTITLEMENTS,
    }),
  },
  school: {
    key: 'school',
    name: 'Skole',
    perStudent: { min: 150, max: 5000 },
    prices: [
      { lookupKey: 'aimfora_school_year', unitAmount: kr(69), interval: 'year', credits: 8 },
    ],
    entitlements: () => ({
      submissionsPerPeriod: 8,
      topupsAllowed: true,
      maxMembers: null,
      doubleGradingAllowed: true,
      unlimitedCredits: false,
    }),
  },
  beta: {
    key: 'beta',
    name: 'Beta',
    prices: [],
    entitlements: () => ({
      submissionsPerPeriod: 0,
      topupsAllowed: false,
      maxMembers: null,
      doubleGradingAllowed: true,
      unlimitedCredits: true,
    }),
  },
}

export interface TopupPack {
  lookupKey: string
  name: string
  /** In øre, excluding VAT, for one pack. */
  unitAmount: number
  credits: number
  /** Most packs in one purchase. */
  maxPacks: number
}

export const TOPUP_PACK: TopupPack = {
  lookupKey: 'aimfora_topup_50',
  name: 'Ekstra besvarelser (50)',
  unitAmount: kr(449),
  credits: 50,
  maxPacks: 10,
}

/** Stripe products. `key` goes in the product's `metadata.aimfora_key`. */
export interface CatalogProduct {
  key: string
  name: string
  description: string
  prices: readonly { lookupKey: string; unitAmount: number; interval: PlanInterval | null }[]
}

export const CATALOG_PRODUCTS: readonly CatalogProduct[] = [
  {
    key: 'aimfora_teacher',
    name: 'AImforA Lærer',
    description: 'Rettehjælp til én lærer',
    prices: PLANS.teacher.prices.map((p) => ({
      lookupKey: p.lookupKey,
      unitAmount: p.unitAmount,
      interval: p.interval,
    })),
  },
  {
    key: 'aimfora_school',
    name: 'AImforA Skole',
    description: 'Rettehjælp til en hel skole, pris pr. elev',
    prices: PLANS.school.prices.map((p) => ({
      lookupKey: p.lookupKey,
      unitAmount: p.unitAmount,
      interval: p.interval,
    })),
  },
  {
    key: 'aimfora_topup',
    name: 'AImforA ekstra besvarelser',
    description: 'Pakke med 50 ekstra besvarelser',
    prices: [
      { lookupKey: TOPUP_PACK.lookupKey, unitAmount: TOPUP_PACK.unitAmount, interval: null },
    ],
  },
]

export interface ResolvedPrice {
  plan: Plan
  price: PlanPrice
}

/** The subscription price behind a Stripe lookup key, or null (also for the top-up pack). */
export function planPriceByLookupKey(lookupKey: string): ResolvedPrice | null {
  for (const plan of Object.values(PLANS)) {
    const price = plan.prices.find((candidate) => candidate.lookupKey === lookupKey)
    if (price) return { plan, price }
  }
  return null
}

export function planPrice(plan: PlanKey, interval: PlanInterval): PlanPrice | null {
  return PLANS[plan].prices.find((price) => price.interval === interval) ?? null
}

/** Every lookup key the catalog uses. */
export const ALL_LOOKUP_KEYS: readonly string[] = CATALOG_PRODUCTS.flatMap((product) =>
  product.prices.map((price) => price.lookupKey),
)

/** Credits a paid subscription line grants: credits per unit times the quantity (students). */
export function creditsForSubscriptionLine(price: PlanPrice, quantity: number): number {
  return price.credits * Math.max(0, Math.trunc(quantity))
}

export function creditsForTopup(packs: number): number {
  return TOPUP_PACK.credits * Math.max(0, Math.trunc(packs))
}

export const topupAmount = (packs: number): number => TOPUP_PACK.unitAmount * packs

/** A valid number of students for the Skole plan. */
export function isValidSchoolQuantity(quantity: number): boolean {
  const { min, max } = PLANS.school.perStudent ?? { min: 1, max: 1 }
  return Number.isInteger(quantity) && quantity >= min && quantity <= max
}

export function isValidPackCount(packs: number): boolean {
  return Number.isInteger(packs) && packs >= 1 && packs <= TOPUP_PACK.maxPacks
}

/** Plan statuses that count as having that plan. Everything else falls back to Prøv. */
export const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'] as const
export type SubscriptionStatus =
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'paused'

export const isActiveSubscription = (status: string): boolean =>
  (ACTIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)

/**
 * How far along its life a subscription is. Stripe stamps events with whole seconds, so two events
 * of one subscription can share a second and arrive in either order; the one further along wins,
 * so a late `created` (`incomplete`) cannot undo an `updated` (`active`) from the same second, and
 * nothing brings back a subscription that has ended. Unknown statuses rank with the live ones.
 */
export function subscriptionStatusRank(status: string): number {
  switch (status) {
    case 'incomplete':
      return 0
    case 'incomplete_expired':
      return 1
    case 'canceled':
      return 4
    case 'unpaid':
    case 'paused':
      return 3
    default:
      return 2
  }
}

/** What an organisation currently has: its paid plan if the subscription counts, otherwise Prøv. */
export interface ResolvedPlan {
  key: PlanKey
  interval: PlanInterval | null
  quantity: number
  entitlements: Entitlements
}

export function resolvePlan(
  subscription: {
    status: string
    plan: PlanKey
    interval: PlanInterval
    quantity: number
  } | null,
): ResolvedPlan {
  if (subscription && isActiveSubscription(subscription.status) && subscription.plan !== 'trial') {
    return {
      key: subscription.plan,
      interval: subscription.interval,
      quantity: subscription.quantity,
      entitlements: PLANS[subscription.plan].entitlements(subscription.interval),
    }
  }
  return { key: 'trial', interval: null, quantity: 1, entitlements: PLANS.trial.entitlements(null) }
}

/** Format øre as kroner for display: `1.290 kr.` (no decimals when whole). */
export function formatKroner(ore: number): string {
  const kroner = ore / 100
  const whole = Number.isInteger(kroner)
  return `${kroner.toLocaleString('da-DK', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  })} kr.`
}
